import { BaseDrawArgs, BaseGridCell } from '@glideapps/glide-data-grid'

interface CornerRadius {
  tl: number
  tr: number
  bl: number
  br: number
}

export function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number | CornerRadius
) {
  if (width <= 0 || height <= 0) return
  if (typeof radius === 'number' && radius <= 0) {
    ctx.rect(x, y, width, height)
    return
  }
  if (typeof radius === 'number') {
    radius = { tl: radius, tr: radius, br: radius, bl: radius }
  }

  // restrict radius to a reasonable max
  radius = {
    tl: Math.min(radius.tl, height / 2, width / 2),
    tr: Math.min(radius.tr, height / 2, width / 2),
    bl: Math.min(radius.bl, height / 2, width / 2),
    br: Math.min(radius.br, height / 2, width / 2),
  }

  radius.tl = Math.max(0, radius.tl)
  radius.tr = Math.max(0, radius.tr)
  radius.br = Math.max(0, radius.br)
  radius.bl = Math.max(0, radius.bl)

  ctx.moveTo(x + radius.tl, y)
  ctx.arcTo(x + width, y, x + width, y + radius.tr, radius.tr)
  ctx.arcTo(x + width, y + height, x + width - radius.br, y + height, radius.br)
  ctx.arcTo(x, y + height, x, y + height - radius.bl, radius.bl)
  ctx.arcTo(x, y, x + radius.tl, y, radius.tl)
}

const itemMargin = 4

export function drawImage(
  args: BaseDrawArgs,
  data: readonly string[],
  rounding: number,
  contentAlign?: BaseGridCell['contentAlign']
) {
  const { rect, col, row, theme, ctx, imageLoader } = args
  const { x, y, height: h, width: w } = rect

  const imgHeight = h - theme.cellVerticalPadding * 2
  const images: (HTMLImageElement | ImageBitmap)[] = []
  let totalWidth = 0

  for (let index = 0; index < data.length; index++) {
    const i = data[index]
    if (!i) continue
    if (i.length === 0) continue
    const img = imageLoader.loadOrGetImage(i, col, row)

    if (img !== undefined) {
      images[index] = img
      const imgWidth = img.width * (imgHeight / img.height)
      totalWidth += imgWidth + itemMargin
    }
  }

  if (totalWidth === 0) return
  totalWidth -= itemMargin

  let drawX = x + theme.cellHorizontalPadding
  if (contentAlign === 'right') {
    drawX = Math.floor(x + w - theme.cellHorizontalPadding - totalWidth)
  } else if (contentAlign === 'center') {
    drawX = Math.floor(x + w / 2 - totalWidth / 2)
  }

  const squareSize = 2
  const gap = 0
  const [img] = images

  if (img) {
    // rows should be width divided by squareSize
    const rows = Math.floor(imgHeight / (squareSize + gap))
    // cols should be height divided by squareSize
    const cols = Math.floor(
      (img.width * (imgHeight / img.height)) / (squareSize + gap)
    )

    // Draw the pattern
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        // Calculate position
        const x = col * (squareSize + gap) + drawX
        const _y = row * (squareSize + gap) + y + theme.cellVerticalPadding

        // Set a random color (optional)
        if ((row + col) % 2 === 0) {
          // Gray square
          ctx.fillStyle = theme.bgBubble
        } else {
          // Transparent square (we don't need to draw anything)
          // You might want to draw a border to make it visible
          ctx.fillStyle = 'transparent'
        }
        ctx.fillRect(x, _y, squareSize, squareSize)
      }
    }
  }

  for (const img of images) {
    if (img === undefined) continue //array is sparse
    const imgWidth = img.width * (imgHeight / img.height)
    ctx.drawImage(
      img,
      drawX,
      y + theme.cellVerticalPadding,
      imgWidth,
      imgHeight
    )

    drawX += imgWidth + itemMargin
  }
}
